import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", { withCredentials: true });
      setUser(data);
    } catch {
      const token = localStorage.getItem("asure_token");
      if (token) localStorage.removeItem("asure_token");
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip /auth/me here.
    // AuthCallback exchanges the session_id and establishes the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Admin login (JWT) — existing flow
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("asure_token", data.token);
    setUser(data);
    toast.success(`Welcome, ${data.name}`);
    return data;
  };

  // Emergent Google sign-in start
  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout", {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("asure_token");
    setUser(null);
    toast("Signed out");
  };

  // Called by AuthCallback after successful session-exchange
  const setSessionUser = (u) => setUser(u);

  return (
    <AuthCtx.Provider value={{ user, ready, login, googleLogin, logout, checkAuth, setSessionUser }}>
      {children}
    </AuthCtx.Provider>
  );
}
